// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import type { GoodwillTooltipType, ImpactedLocomotiveType } from '../../../../../types/apolloType';

type Props = {
  coqueData: GoodwillTooltipType,
  storeName: string,
  isLocomotive: boolean,
  format: (number) => string,
}

export default function GoodwillVisioglobeTooltip({
  coqueData, storeName, isLocomotive, format,
}: Props) {
  const proximityImpactTooltipSection = () => (
    <div className="goodwill-tooltip-section">
      <b>
        <Translate
          value="goodwill.tooltip.proximity.title"
          ratioValue={format(coqueData.proximityImpact)}
        />
      </b>
      <div className="goodwill-tooltip-section-content">
        <Translate value="goodwill.tooltip.proximity.top5OtherLocomotives" />
        <Translate
          value="goodwill.tooltip.proximity.average"
          ratioValue={format(coqueData.avgProximityImpactTop5OtherLocomotives)}
        />
        <Translate
          value="goodwill.tooltip.proximity.sum"
          ratioValue={format(coqueData.sumProximityImpactTop5OtherLocomotives)}
        />
      </div>
    </div>
  );

  const totalImpactTooltipSection = () => (
    <div className="goodwill-tooltip-section">
      <b>
        <Translate
          value="goodwill.tooltip.total.title"
          ratioValue={format(coqueData.totalImpact)}
        />
      </b>
      <div className="goodwill-tooltip-section-content">
        <Translate value="goodwill.tooltip.total.top5OtherLocomotives" />
        <Translate
          value="goodwill.tooltip.total.average"
          ratioValue={format(coqueData.avgTotalImpactTop5OtherLocomotives)}
        />
        <Translate
          value="goodwill.tooltip.total.sum"
          ratioValue={format(coqueData.sumTotalImpactTop5OtherLocomotives)}
        />
      </div>
    </div>
  );

  const impactedLocomotiveSection = (locomotive: ImpactedLocomotiveType) => (
    <span key={locomotive.locomotiveName} className="impacted-locomotives_line">
      {`${locomotive.locomotiveName}: ${format(locomotive.impactValue)}`}
    </span>
  );

  const synergicImpactTooltipSection = () => (
    <div className="goodwill-tooltip-section">
      <b>
        <Translate
          value="goodwill.tooltip.synergic.title"
          ratioValue={format(coqueData.synergicImpact)}
        />
      </b>
      <div className="goodwill-tooltip-section-content">
        <Translate
          value="goodwill.tooltip.synergic.bl3BranchName"
          bl3Name={coqueData.bl3BranchName}
        />
        <Translate value="goodwill.tooltip.synergic.impactedLocomotives" />
        <div className="impacted-locomotives-container">
          {
            coqueData.synergizingLocomotives
              .map(locomotive => impactedLocomotiveSection(locomotive))
          }
        </div>
      </div>
    </div>
  );

  const systematicImpactTooltipSection = () => (
    <div className="goodwill-tooltip-section">
      <b>
        <Translate
          value="goodwill.tooltip.systematic.title"
          ratioValue={format(coqueData.systematicImpact)}
        />
      </b>
      <div className="goodwill-tooltip-section-content">
        <Translate value="goodwill.tooltip.synergic.impactedLocomotives" />
        <div className="impacted-locomotives-container">
          {
            coqueData.attractiveLocomotives
              .map(locomotive => impactedLocomotiveSection(locomotive))
          }
        </div>
      </div>
    </div>
  );

  const locomotiveTooltipSection = () => (
    <div className="goodwill-tooltip-section">
      <b><Translate value="goodwill.tooltip.locomotive.title" /></b>
      <div className="goodwill-tooltip-section-content">
        <Translate
          value="goodwill.tooltip.locomotive.proximity"
          ratioValue={format(coqueData.proximityImpact)}
        />
        <Translate
          value="goodwill.tooltip.locomotive.synergic"
          ratioValue={format(coqueData.synergicImpact)}
        />
        <Translate
          value="goodwill.tooltip.locomotive.systematic"
          ratioValue={format(coqueData.systematicImpact)}
        />
        <Translate
          value="goodwill.tooltip.locomotive.total"
          ratioValue={format(coqueData.totalImpact)}
        />
        <Translate
          value="goodwill.tooltip.locomotive.totalGoodwillReachableAfterCeiling"
          ratioValue={format(coqueData.totalGoodwillReachableAfterCeiling)}
        />
      </div>
    </div>
  );

  return (
    <div className="goodwill-popup-container">
      <div className="goodwill-popup">
        <div className="popup-title-container">
          <span className="popup-title">{storeName}</span>
          {/* Closing is handled in mapviewer with jQuery */}
          <div id="goodwill_popup_close_button">
            <i className="fa fa-times" />
          </div>
        </div>
        <div className="popup-content">
          {
            coqueData && (isLocomotive ?
                (locomotiveTooltipSection())
                : (
                  <div className="store-impacts-container">
                    {coqueData.proximityImpact > 0 && proximityImpactTooltipSection()}
                    {coqueData.synergicImpact > 0 && synergicImpactTooltipSection()}
                    {coqueData.systematicImpact > 0 && systematicImpactTooltipSection()}
                    {coqueData.totalImpact > 0 && totalImpactTooltipSection()}
                  </div>
                )
            )
          }
        </div>
      </div>
    </div>
  );
}
